/**
 * POLAR draft-lock gate (AutoCAD POLARMODE / F10 + POLARANG) — the impure wrapper
 * around the pure {@link applyPolarConstraint} geometry helper. Sibling of
 * {@link ./orthoDraftLock}: it owns the single place that reads the live polar
 * sysvar / preference state and hands the pointer-input seam one function.
 *
 * Precedence + gating (ALL must hold for the polar lock to engage):
 *   - `snapMagnetized` is false — object snap OVERRIDES polar tracking in AutoCAD
 *     (a magnet-snapped point goes to the geometry, never a polar ray). Like
 *     ortho, this gates on whether the emit was ACTUALLY magnetized (AUTOSNAP
 *     bit 4), not mere hit presence: a marker-only hit still leaves the raw
 *     cursor, which must still be polar-tracked;
 *   - `base` is non-null — an anchor ($LASTPOINT) exists; polar tracking never
 *     affects the FIRST point of a command (no reference direction to measure);
 *   - POLAR is EFFECTIVE — the `POLARMODE` master bit 0 is set OR a temp override
 *     forces it (via `selectEffectivePolar`; config-only bits like POLARADDANG do
 *     NOT by themselves enable polar — #492 @codex P2);
 *   - ORTHO is NOT effective — ORTHO and POLAR are MUTUALLY EXCLUSIVE in AutoCAD
 *     (enabling one disables the other). The exclusion is enforced as a STATE
 *     TRANSITION at the root (`sysvarStore.setVar` clears the sibling aid on every
 *     enable path — #492 @codex P2), so the base sysvars are never both set. This
 *     read-time guard remains ONLY for the momentary corner where a temp Shift
 *     ORTHO override is held while POLARMODE is on: the ortho lock (applied first
 *     at the call-site) wins and polar stands down, so the cursor is never
 *     double-constrained.
 *   - a tracking ray is within the angular threshold — polar tracking is a SOFT
 *     snap; outside the threshold the cursor stays free (unlike the hard ortho
 *     lock).
 * Otherwise the cursor is returned verbatim with no guide, so every polar-off /
 * non-draft / snapped / free-cursor path is byte-identical to the pre-feature
 * behaviour.
 *
 * The returned `guide` carries the world-space endpoints for the polar tracking
 * line overlay AND the "distance < angle°" tooltip text; `null` means polar did
 * NOT geometrically engage (no ray within the threshold) — the caller must use
 * `guide != null`, never `pathVisible`, as the "did polar lock" signal (local
 * Codex review round 1 P1 — an earlier revision conflated the two, so TRACKPATH
 * suppressing the display line accidentally discarded the LOCKED `world` too).
 * `pathVisible` (TRACKPATH bit 2) governs ONLY whether the alignment-path LINE
 * should render when `guide` is non-null — never `world`, and never the
 * tooltip: AutoCAD's TRACKPATH is documented as gating the alignment PATH only
 * ("does not display polar tracking path"); the AutoTrack tooltip is a
 * different, un-gated-in-this-app UI class (local Codex review round 1 P2).
 */

import {
  applyPolarConstraint,
  buildPolarAngleTable,
  DEFAULT_POLAR_THRESHOLD_DEG,
  norm360,
  type PolarTrackingConfig,
} from "./polarConstraint";
import {
  useSysvarStore,
  selectEffectivePolar,
  selectEffectiveOrtho,
  selectPolarAddAngEnabled,
  selectPolarAngleBasis,
  selectPolarTrackPathVisible,
} from "../../dynamicInput/sysvarStore";
import { usePolarTrackingPrefs } from "../../state/polarTrackingPrefs";

export interface PolarGuideInfo {
  readonly from: { x: number; y: number };
  readonly to: { x: number; y: number };
  /**
   * Reported angle in AutoCAD's "angle < distance" tooltip sense — measured
   * in the user's SELECTED basis (`POLARMODE`'s relative-basis bit), NOT
   * necessarily the world/UCS angle used to compute `to` (local Codex
   * review, post-N6-rebase P2). Under "absolute" basis this equals the
   * world angle (0° = +X). Under "relative" basis this is the delta from
   * `referenceAngleDeg` (the previous segment's direction) — e.g. a ray
   * that continues straight ahead reports 0°, never the previous segment's
   * own world heading. `to`/`from` are always world-space regardless of
   * basis; only this display value and `tip` rotate with the basis.
   */
  readonly angleDeg: number;
  readonly distance: number;
  /** Rendered "distance < angle°" tooltip text (locale-neutral numeric). */
  readonly tip: string;
}

export interface PolarDraftLock {
  readonly world: { x: number; y: number };
  /** Non-null iff polar geometrically ENGAGED (a ray locked) — the sole "did polar lock" signal. */
  readonly guide: PolarGuideInfo | null;
  /**
   * TRACKPATH bit 2 ("no polar tracking path") — whether the dashed
   * alignment-path LINE should render. Meaningless when `guide` is null.
   * Independent of `world`/`guide.tip`: AutoCAD's TRACKPATH is a pure
   * display gate on the path line only (never the lock, never the tooltip).
   */
  readonly pathVisible: boolean;
}

/** Format the polar readout "distance < angle°" (AutoCAD polar-tooltip parity). */
export function formatPolarTip(distance: number, angleDeg: number): string {
  const dist = Math.abs(distance).toFixed(4);
  const angle = Number.isInteger(angleDeg)
    ? String(angleDeg)
    : angleDeg.toFixed(4);
  return `${dist} < ${angle}°`;
}

/**
 * Lock a resolved cursor `world` onto the nearest polar tracking ray from the
 * draft `base` anchor when polar tracking is effective (and ortho is not) and the
 * emit was not magnet-snapped; otherwise return `world` unchanged with no guide.
 * Reads the live POLARMODE / POLARANG / temp-override + the additional-angle
 * preferences at event time (not via a hook).
 *
 * `referenceAngleDeg` (N6 POLAR-RELATIVE-BASIS-WIRE) — the direction of the
 * last segment drawn (`draftSharedHelpers.polarReferenceAngleForDraftSession`),
 * or `null` when there is none (first segment of the command). Only consulted
 * when POLARMODE's relative-basis bit is set (`selectPolarAngleBasis`); a
 * `null` reference under relative basis degrades to the same 0° rotation as
 * absolute basis (`buildPolarAngleTable`'s default), matching AutoCAD's real
 * behaviour for a relative-basis FIRST segment.
 */
export function polarLockedCursor(
  world: { x: number; y: number },
  base: { x: number; y: number } | null,
  snapMagnetized: boolean,
  referenceAngleDeg: number | null = null,
): PolarDraftLock {
  const passthrough: PolarDraftLock = { world, guide: null, pathVisible: true };
  if (snapMagnetized) return passthrough;
  if (!base) return passthrough;
  const sysvar = useSysvarStore.getState();
  if (!selectEffectivePolar(sysvar)) return passthrough;
  // Mutual exclusion: ortho (applied first) wins when both are somehow on.
  if (selectEffectiveOrtho(sysvar)) return passthrough;

  const prefs = usePolarTrackingPrefs.getState();
  const config: PolarTrackingConfig = {
    incrementAngleDeg: sysvar.values.POLARANG,
    additionalAnglesDeg: prefs.additionalAnglesDeg,
    // POLARMODE bit 4 (POLARADDANG) is the single source of the "use additional
    // angles" flag; the angle LIST is the POLARADDANG string store (#492 P2).
    useAdditionalAngles: selectPolarAddAngEnabled(sysvar),
    // N6 — POLARMODE's app-extension relative-basis bit (see
    // `POLARMODE_RELATIVE_BASIS_BIT`'s doc for why it is bit 16, not
    // AutoCAD's real bit 1) is the single source of truth for the basis.
    angleBasis: selectPolarAngleBasis(sysvar),
  };
  const table = buildPolarAngleTable(config, referenceAngleDeg ?? 0);
  const result = applyPolarConstraint(
    base,
    world,
    table,
    DEFAULT_POLAR_THRESHOLD_DEG,
  );
  if (result.lockedAngleDeg === null) return passthrough;

  const to = { x: result.x, y: result.y };
  // Reported/display angle vs. world angle (local Codex review, post-N6-rebase
  // P2): `result.lockedAngleDeg` is ALWAYS world/UCS-space — it is what
  // `applyPolarConstraint` used to project `to`, and `buildPolarAngleTable`
  // already baked the relative-basis rotation INTO the table entries so the
  // projection lands on the correct world ray regardless of basis. But
  // AutoCAD's on-screen polar tooltip reports the angle in the SELECTED
  // basis, not always the world angle: under "relative" basis the tooltip
  // shows the delta from the previous segment (0° = continuing straight
  // ahead), never the segment's own world heading. Subtracting
  // `referenceAngleDeg` back out (then re-normalising — the subtraction can
  // land outside [0, 360) since `referenceAngleDeg` comes straight from
  // `Math.atan2`, range (-180, 180]) recovers exactly that delta. A `null`
  // reference (first segment) already degraded the table to the same 0°
  // rotation as absolute basis, so the guard below is a no-op in that case —
  // written explicitly rather than relying on `- 0` for clarity.
  const displayAngleDeg =
    config.angleBasis === "relative" && referenceAngleDeg !== null
      ? norm360(result.lockedAngleDeg - referenceAngleDeg)
      : result.lockedAngleDeg;
  // `guide` (and therefore `world`) is ALWAYS populated once a ray locks —
  // TRACKPATH never reaches this branch's engagement decision (local Codex
  // review round 1 P1). Only `pathVisible` (TRACKPATH bit 2) varies; the
  // caller (`resolveDraftAidConstraint`) uses it to gate the LINE only,
  // leaving `guide.tip` (the AutoTrack tooltip) always available when engaged.
  return {
    world: to,
    pathVisible: selectPolarTrackPathVisible(sysvar),
    guide: {
      from: { x: base.x, y: base.y },
      to,
      angleDeg: displayAngleDeg,
      distance: result.distance,
      tip: formatPolarTip(result.distance, displayAngleDeg),
    },
  };
}
